// NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useParams } from "react-router-dom";
import { isNull, omitBy, isEmpty, map } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { ServiceTypeCreateSchema } from "./service-type-form-validation";

// STYLE
import "./service-type-form.scss";

// ANT COMPONENTS
import { Col, Row, Checkbox } from "antd";
import {
  AlertBox,
  Button,
  DateRange,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import useSession from "Hooks/session.hook";
import { OrderSettingsEnum } from "Data/settings";

interface ServiceTypeFormComponentProps {
  info?: any;
  onFetch: any;
  settingOptions?: any;
  onSubmit?: any;
  onUpdate?: any;
}

const ServiceTypeFormComponent: React.FC<ServiceTypeFormComponentProps> = ({
  info,
  onFetch,
  settingOptions,
  onSubmit,
  onUpdate,
}) => {
  const { org } = useSession();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const initialInput = {
    name: "",
    isFixedAmount: false,
    fixedAmount: "",
    isPercentage: false,
    percentage: "",
    capAmount: "",
    validFrom: {
      date: null,
      dateString: "",
    },
    validTo: {
      date: moment("31/12/9999", "DD/MM/YYYY"),
      dateString: moment("31/12/9999", "DD/MM/YYYY").toISOString(),
    },
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);

  useEffect(() => {
    onFetch({
      orgID: org._id,
      type: OrderSettingsEnum.SERVICE_TYPE,
      currentVersionOnly: true,
    });
    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    if (info) {
      setInput({
        ...input,
        ...info,
        validFrom: { date: info?.validFrom, dateString: moment(info?.validFrom).toISOString() },
        validTo: { date: info?.validTo, dateString: moment(info?.validTo).toISOString() },
      });
    }
  }, [info]);

  const handleSelectChange = (value: string | string[], name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (checked: boolean, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: checked });
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setError(initialError);
      setInput({ ...input, validFrom: startDate });
    } else if (endDate.date) {
      setError(initialError);
      setInput({ ...input, validTo: endDate });
    }
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        settingID: info?._id,
        surcharge: omitBy(
          {
            // selected but empty attr is unvalidated if pair is valid
            fixedAmount: input.isFixedAmount ? input.fixedAmount : "",
            percentage: input.isPercentage ? input.percentage : "",
            capAmount: input.capAmount,
          },
          isEmpty,
        ),
        validFrom: moment(input?.validFrom?.date).toISOString(),
        validTo: moment(input?.validTo?.date).toISOString(),
      };
      const value = ServiceTypeCreateSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });
      info ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="service-type-form">
      <form>
        <div className="main-header">
          <span className="center header-title">{t("label.serviceType").toUpperCase()}</span>
        </div>
        <Row gutter={[20, 0]} className="mt-40" justify="center">
          <Col xs={24} sm={24} md={12} className="mb-20">
            <SelectInput
              showSearch
              isRequired={true}
              isReadonly={input.name}
              label={t("label.serviceType")}
              placeholder={t("txt.selectHere")}
              value={input.name}
              options={map(settingOptions[OrderSettingsEnum.SERVICE_TYPE], (service) => {
                return {
                  label: service?.name,
                  value: service?.name,
                };
              })}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="label"
              onChange={(value) => handleSelectChange(value, "name")}
            />
            <AlertBox text={error.name} />
          </Col>
        </Row>
        <Row gutter={[20, 0]} className="mt-10" justify="center">
          <Col xs={24} sm={24} md={12} className="mb-20">
            <label className="inp-label inp-label--required">{t("label.surcharge")}</label>
            <Row gutter={[20, 0]} align="middle">
              <Col xs={24} sm={24} md={10} className="mb-9 pr-0">
                <Checkbox
                  checked={input.isFixedAmount}
                  onChange={(e) => handleCheckboxChange(e.target.checked, "isFixedAmount")}
                >
                  {t("label.fixedAmount")}
                </Checkbox>
              </Col>
              <Col xs={24} sm={24} md={14} className="mb-9">
                <TextInput
                  isRequired={true}
                  isReadonly={!input.isFixedAmount}
                  prefix="RM"
                  value={input.fixedAmount}
                  name="fixedAmount"
                  onChange={handleFieldChange}
                />
              </Col>
            </Row>
            <Row gutter={[18, 0]} align="middle">
              <Col xs={24} sm={24} md={10} className="mb-9 pr-0">
                <Checkbox
                  checked={input.isPercentage}
                  onChange={(e) => handleCheckboxChange(e.target.checked, "isPercentage")}
                >
                  {t("label.percentage")}
                </Checkbox>
              </Col>
              <Col xs={24} sm={24} md={14} className="mb-0">
                <TextInput
                  isRequired={true}
                  isReadonly={!input.isPercentage}
                  value={input.percentage}
                  name="percentage"
                  suffix="%"
                  onChange={handleFieldChange}
                />
              </Col>
            </Row>
            {input.isPercentage ? (
              <Row gutter={[20, 0]} align="middle">
                <Col xs={24} sm={24} md={10} className="mt-10 mb-0 pr-0">
                  <label className="ml-24">{t(`label.capAmount`)}</label>
                </Col>
                <Col xs={24} sm={24} md={14} className="mt-10 mb-0">
                  <TextInput
                    isRequired={true}
                    prefix={t("txt.rm")}
                    value={input.capAmount}
                    name="capAmount"
                    onChange={handleFieldChange}
                  />
                </Col>
              </Row>
            ) : (
              <></>
            )}
            <Row>
              <Col span={24} className="mb-0 alert-box">
                <AlertBox
                  text={
                    error["surcharge.fixedAmount"] || error["surcharge.percentage"]
                      ? t("error.surcharge")
                      : error["surcharge.capAmount"]
                      ? t(`error.pattern`, { field: t(`label.capAmount`) })
                      : null
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[20, 0]} className="mt-10" align="bottom" justify="center">
          <Col xs={24} sm={24} md={12} className="mb-0">
            <DateRange
              isRequired={true}
              startLabel={t("label.validFrom")}
              endLabel={t("label.validTo")}
              value={{
                startDate: input.validFrom,
                endDate: input.validTo,
              }}
              onChange={handleDateChange}
            />
            <AlertBox text={error.validFrom || error.validTo} />
          </Col>
        </Row>
        <Row className="mt-50" justify="center">
          <Col className="mb-50">
            <Button
              size="large"
              type="primary"
              onClick={handleSubmit}
              label={id ? t("action.update") : t("action.create")}
            />
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default ServiceTypeFormComponent;
